
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { TipsTable } from "./TipsTable";
import { TipsTableHeader } from "./tips-table/TipsTableHeader";
import { TipDialogManager } from "./tips-dialog/TipDialogManager";
import { Tip } from "@/types";

export function TipsManagement({ tips: initialTips, onRefresh }: { tips: Tip[], onRefresh: () => void }) {
  const { toast } = useToast();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingTip, setEditingTip] = useState<Tip | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [universityFilter, setUniversityFilter] = useState("all_tips");

  const handleClearVotes = async (id: number) => {
    try {
      console.log('Clearing votes for tip:', id);
      const { error } = await supabase
        .from("tips")
        .update({ unsw_votes: 0, usyd_votes: 0, uoa_votes: 0 })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ 
        title: "Success", 
        description: "Votes cleared successfully" 
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error clearing votes:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to clear votes",
      });
    }
  };

  const handleArchive = async (id: number) => {
    try {
      const { error } = await supabase
        .from("tips")
        .update({ archived: true })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ 
        title: "Success", 
        description: "Tip archived successfully" 
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error archiving tip:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to archive tip",
      });
    }
  };

  const handleUnarchive = async (id: number) => {
    try {
      const { error } = await supabase
        .from("tips")
        .update({ archived: false })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ 
        title: "Success", 
        description: "Tip unarchived successfully" 
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error unarchiving tip:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to unarchive tip",
      });
    }
  };

  const handleApprove = async (id: number) => {
    try {
      const { error } = await supabase
        .from("tips")
        .update({ status: 'approved' })
        .eq("id", id);
      
      if (error) throw error;
      
      toast({ 
        title: "Success", 
        description: "Tip approved successfully" 
      });
      onRefresh();
    } catch (error: any) {
      console.error("Error approving tip:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to approve tip",
      });
    }
  };

  const handleSubmit = async (data: { 
    title: string; 
    content: string; 
    status: string; 
    emoji: string;
    universityId: number | null;
  }) => {
    setFormError(null);

    try {
      if (editingTip) {
        const scope = data.universityId ? 'university_specific' as const : 'all_universities' as const;
        const updateData = {
          title: data.title,
          content: data.content,
          status: data.status,
          emoji: data.emoji,
          university_id: data.universityId,
          scope
        };

        const { error } = await supabase
          .from("tips")
          .update(updateData)
          .eq("id", editingTip.id);
        
        if (error) throw error;
        
        toast({ title: "Success", description: "Tip updated successfully" });
      } else {
        const scope = data.universityId ? 'university_specific' as const : 'all_universities' as const;
        const { error } = await supabase
          .from("tips")
          .insert([{ 
            title: data.title,
            content: data.content,
            status: 'draft',
            emoji: data.emoji,
            university_id: data.universityId,
            scope
          }]);
        
        if (error) throw error;
        
        toast({ title: "Success", description: "Tip added successfully" });
      }
      
      setIsAddOpen(false);
      setIsEditOpen(false);
      setEditingTip(null);
      onRefresh();
    } catch (error: any) {
      console.error("Error saving tip:", error);
      setFormError(error.message || "Failed to save tip");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save tip",
      });
    }
  };

  const handleEdit = (tip: Tip) => {
    setEditingTip(tip);
    setIsEditOpen(true);
  };

  const filteredTips = initialTips?.filter(tip => {
    // First check archived status
    if (!showArchived && tip.archived) return false;
    
    // Then check status filter
    if (statusFilter !== "all" && tip.status !== statusFilter) return false;
    
    // Handle university filter
    if (universityFilter === "all_tips") {
      // Show all tips regardless of scope or university
      return true;
    } else if (universityFilter === "all_universities") {
      // Show only tips with scope 'all_universities'
      return tip.scope === 'all_universities';
    } else {
      // Show tips for specific university
      return tip.university_id === parseInt(universityFilter);
    }
  }) || [];

  if (!initialTips) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>No tips data available</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <TipsTableHeader
        showArchived={showArchived}
        onToggleArchived={() => setShowArchived(!showArchived)}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        universityFilter={universityFilter}
        onUniversityFilterChange={setUniversityFilter}
        onAddTip={() => {
          setEditingTip(null);
          setIsAddOpen(true);
        }}
      />

      <TipDialogManager
        isAddOpen={isAddOpen}
        setIsAddOpen={setIsAddOpen}
        isEditOpen={isEditOpen}
        setIsEditOpen={setIsEditOpen}
        editingTip={editingTip}
        formError={formError}
        onSubmit={handleSubmit}
      />

      <TipsTable
        tips={filteredTips}
        onEdit={handleEdit}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onApprove={handleApprove}
        onClearVotes={handleClearVotes}
        showArchived={showArchived}
      />
    </Card>
  );
}
